import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";
import { HiChevronDown } from "react-icons/hi";

import invoiceGenerate from "../../assets/svg/invoice-generate.svg";
import radioCheck from "../../assets/svg/radio-check.svg";
import DateTimePicker from "../../components/DateTimePicker";
import {
  generateOppositionEvidenceAffidavitPdf,
  getOppositionEvidenceAffidavit,
  saveOppositionEvidenceAffidavit,
  type OppositionEvidenceAffidavitItem,
} from "../../api/opposition";
import { downloadFileFromUrlWithFallback } from "../../utils/downloadFileFromUrl";

export const OPPOSITION_PREPARATION_OF_EVIDENCE_AFFIDAVIT_FORM_ID =
  "opposition-preparation-of-evidence-affidavit-form";

const FORM_STYLES = { fontFamily: "Inter, sans-serif" } as const;
const LABEL_CLASS = "text-[12px] font-normal leading-5 text-[#222222]";
const INPUT_CLASS =
  "h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-white px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222] box-border";
const TEXTAREA_CLASS =
  "min-h-[80px] w-full rounded-[6px] border border-[#EEEEEE] bg-white px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222] resize-y";

function RadioCircle({ active }: { active: boolean }) {
  if (active) return <img src={radioCheck} alt="" width={14} height={14} />;
  return (
    <span
      aria-hidden
      className="inline-block w-[14px] h-[14px] rounded-full border border-[#C3C3C3] bg-white"
    />
  );
}

function parseISODate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d.getTime()) ? null : d;
}

function toISODateString(date: Date | null): string | null {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

type ParaReply = { heading: string; text: string };
type Draft = {
  noOfParagraphsInConflictingIntro: string;
  paragraphWiseReplies: ParaReply[];
  nameOfOpponentsAttorney: string;
  addressOfOpponentsAttorney: string;
  emailOfOpponentsAttorney: string;
  dateOfEvidenceAffidavit: Date | null;
};

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function OppositionPreparationOfEvidenceAffidavitView({
  leadId,
  onSuccess,
}: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [items, setItems] = useState<OppositionEvidenceAffidavitItem[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [activeIdx, setActiveIdx] = useState(0);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getOppositionEvidenceAffidavit(leadId)
      .then((data) => {
        if (cancelled) return;
        setItems(data.evidenceAffidavits);
        const next: Record<string, Draft> = {};
        data.evidenceAffidavits.forEach((it) => {
          next[it.conflictingMarkId] = {
            noOfParagraphsInConflictingIntro:
              it.noOfParagraphsInConflictingIntro != null
                ? String(it.noOfParagraphsInConflictingIntro)
                : "",
            paragraphWiseReplies:
              it.paragraphWiseReplies && it.paragraphWiseReplies.length
                ? it.paragraphWiseReplies.map((r) => ({
                    heading: r.heading ?? "",
                    text: r.text ?? "",
                  }))
                : [{ heading: "", text: "" }],
            nameOfOpponentsAttorney: it.nameOfOpponentsAttorney ?? "",
            addressOfOpponentsAttorney: it.addressOfOpponentsAttorney ?? "",
            emailOfOpponentsAttorney: it.emailOfOpponentsAttorney ?? "",
            dateOfEvidenceAffidavit: parseISODate(it.dateOfEvidenceAffidavit),
          };
        });
        setDrafts(next);
        setActiveIdx(0);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Evidence Affidavit"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const activeItem = items[activeIdx] ?? null;
  const activeDraft = activeItem
    ? drafts[activeItem.conflictingMarkId] ?? null
    : null;

  const updateDraft = (patch: Partial<Draft>) => {
    if (!activeItem) return;
    setDrafts((prev) => ({
      ...prev,
      [activeItem.conflictingMarkId]: {
        ...prev[activeItem.conflictingMarkId],
        ...patch,
      },
    }));
  };

  const updateReply = (idx: number, patch: Partial<ParaReply>) => {
    if (!activeDraft) return;
    const next = [...activeDraft.paragraphWiseReplies];
    next[idx] = { ...next[idx], ...patch };
    updateDraft({ paragraphWiseReplies: next });
  };

  const addReplyRow = () => {
    if (!activeDraft) return;
    updateDraft({
      paragraphWiseReplies: [
        ...activeDraft.paragraphWiseReplies,
        { heading: "", text: "" },
      ],
    });
  };

  const removeReplyRow = (idx: number) => {
    if (!activeDraft) return;
    const next = activeDraft.paragraphWiseReplies.filter((_, i) => i !== idx);
    updateDraft({
      paragraphWiseReplies: next.length ? next : [{ heading: "", text: "" }],
    });
  };

  const persist = async (): Promise<boolean> => {
    if (!leadId || items.length === 0) return false;
    try {
      const fresh = await saveOppositionEvidenceAffidavit(leadId, {
        applications: items.map((it) => {
          const d = drafts[it.conflictingMarkId]!;
          const replies = d.paragraphWiseReplies.filter(
            (r) => r.heading.trim() !== "" || r.text.trim() !== ""
          );
          const paragraphsNum = d.noOfParagraphsInConflictingIntro.trim();
          return {
            conflictingMarkId: it.conflictingMarkId,
            ...(paragraphsNum === ""
              ? { noOfParagraphsInConflictingIntro: null }
              : {
                  noOfParagraphsInConflictingIntro: Number(paragraphsNum),
                }),
            paragraphWiseReplies: replies,
            nameOfOpponentsAttorney: d.nameOfOpponentsAttorney.trim() || null,
            addressOfOpponentsAttorney:
              d.addressOfOpponentsAttorney.trim() || null,
            emailOfOpponentsAttorney: d.emailOfOpponentsAttorney.trim() || null,
            dateOfEvidenceAffidavit: toISODateString(d.dateOfEvidenceAffidavit),
          };
        }),
      });
      setItems(fresh.evidenceAffidavits);
      return true;
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save Evidence Affidavit"
      );
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    setSaving(true);
    try {
      const ok = await persist();
      if (ok) {
        toast.success("Evidence Affidavit data saved.");
        onSuccess?.(leadId);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (!leadId || !activeItem || generating) return;
    setGenerating(true);
    try {
      const ok = await persist();
      if (!ok) return;
      const pdf = await generateOppositionEvidenceAffidavitPdf(
        leadId,
        activeItem.conflictingMarkId
      );
      setItems((prev) =>
        prev.map((it) =>
          it.conflictingMarkId === activeItem.conflictingMarkId
            ? {
                ...it,
                evidenceAffidavitPdfKey: pdf.s3Key,
                evidenceAffidavitPdfFilename: pdf.filename,
                evidenceAffidavitPdfUrl: pdf.downloadUrl,
              }
            : it
        )
      );
      toast.success("Evidence Affidavit PDF generated.");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to generate PDF"
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!activeItem?.evidenceAffidavitPdfUrl || downloading) return;
    setDownloading(true);
    try {
      const result = await downloadFileFromUrlWithFallback(
        activeItem.evidenceAffidavitPdfUrl,
        activeItem.evidenceAffidavitPdfFilename ?? "evidence-affidavit.pdf"
      );
      if (result === "local") toast.success("PDF saved to your device.");
      else toast.info("Opened the PDF in a new tab.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const handlePreviewPdf = () => {
    if (!activeItem?.evidenceAffidavitPdfUrl) return;
    window.open(
      activeItem.evidenceAffidavitPdfUrl,
      "_blank",
      "noopener,noreferrer"
    );
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12"
        style={FORM_STYLES}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12"
        style={FORM_STYLES}
      >
        <p className="text-sm text-[#6D6D6D]">Loading…</p>
      </div>
    );
  }

  if (items.length === 0 || !activeItem || !activeDraft) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12"
        style={FORM_STYLES}
      >
        <p className="text-sm text-[#6D6D6D]">
          No applications on an ACTIVE Evidence invoice yet. Save Step 6
          (Invoice to Client for Evidence) first.
        </p>
      </div>
    );
  }

  return (
    <form
      id={OPPOSITION_PREPARATION_OF_EVIDENCE_AFFIDAVIT_FORM_ID}
      onSubmit={handleSubmit}
      className="flex w-full max-w-[1085px] flex-col gap-5"
      style={FORM_STYLES}
    >
      {items.length > 1 && (
        <div className="flex flex-row flex-wrap gap-3">
          {items.map((it, idx) => {
            const isActive = idx === activeIdx;
            return (
              <button
                key={it.conflictingMarkId}
                type="button"
                onClick={() => setActiveIdx(idx)}
                className={`flex items-center gap-2 px-2.5 py-2.5 rounded-[6px] h-10 min-w-[140px] transition-colors text-xs ${
                  isActive
                    ? "bg-[#222222] text-white"
                    : "bg-[#F7F7F7] text-[#222222]"
                }`}
              >
                <RadioCircle active={isActive} />
                {it.applicationNo?.trim() || `Application ${it.evidenceAffidavitNo}`}
              </button>
            );
          })}
        </div>
      )}

      {/* Fetched details — collapsible accordion */}
      <div className="w-full rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] overflow-hidden">
        <button
          type="button"
          onClick={() => setDetailsOpen((o) => !o)}
          className="flex w-full items-center justify-between px-3 py-2.5 text-[12px] font-medium text-[#222] hover:bg-[#F7F7F7] transition-colors touch-manipulation min-h-[44px] md:min-h-0"
          style={FORM_STYLES}
        >
          <span>Fetched Details</span>
          <HiChevronDown
            className="w-4 h-4 text-[#6D6D6D] transition-transform duration-200 shrink-0"
            style={{ transform: detailsOpen ? "rotate(180deg)" : "rotate(0deg)" }}
          />
        </button>
        {detailsOpen && (
          <div className="border-t border-[#EEEEEE] flex flex-col gap-3 px-3 py-2.5">
            <div className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-[12px] leading-5">
              {[
                { label: "Mark", value: activeItem.mark ?? "" },
                {
                  label: "Selected Class",
                  value:
                    activeItem.classNumber != null
                      ? String(activeItem.classNumber)
                      : "",
                },
                { label: "Applicant", value: activeItem.applicant ?? "" },
                {
                  label: "Applicant Address",
                  value: activeItem.applicantAddress ?? "",
                },
                {
                  label: "Applicant Type",
                  value: activeItem.applicantType ?? "",
                },
                { label: "User Date", value: activeItem.userDate ?? "" },
                {
                  label: "Signatory Details",
                  value: activeItem.signatoryDetails ?? "",
                },
                {
                  label: "Description of Applicant",
                  value: activeItem.descriptionOfApplicant ?? "",
                },
                {
                  label: "Conflicting Mark",
                  value: activeItem.conflictingMark ?? "",
                },
                {
                  label: "Application No. of Conflicting Mark",
                  value: activeItem.applicationNo ?? "",
                },
                {
                  label: "Class of Conflicting Mark",
                  value:
                    activeItem.classOfConflictingMark != null
                      ? String(activeItem.classOfConflictingMark)
                      : "",
                },
                {
                  label: "Goods/Services of Conflicting Mark",
                  value: activeItem.goodsServicesOfConflictingMark ?? "",
                },
                {
                  label: "Name of Conflicting Proprietor",
                  value: activeItem.nameOfConflictingProprietor ?? "",
                },
                {
                  label: "Address of Conflicting Proprietor",
                  value: activeItem.addressOfConflictingProprietor ?? "",
                },
                {
                  label: "Opposition No.",
                  value: activeItem.oppositionNo ?? "",
                },
                {
                  label: "Date of Notice of Opposition",
                  value: activeItem.dateOfNoticeOfOpposition ?? "",
                },
                {
                  label: "Date of Receipt of Counter",
                  value: activeItem.dateOfReceiptOfCounter ?? "",
                },
                {
                  label: "No. of Exhibits for Evidence",
                  value:
                    activeItem.noOfExhibitsForEvidence != null
                      ? String(activeItem.noOfExhibitsForEvidence)
                      : "",
                },
              ].map((row) => {
                const isEmpty = !row.value;
                return (
                  <Fragment key={row.label}>
                    <div className="flex items-baseline font-medium text-[#222]">
                      {row.label}
                    </div>
                    <div className="flex items-baseline text-[#222]">:</div>
                    <div className="text-[#222] break-words min-w-0">
                      {isEmpty ? (
                        <span className="text-[#6D6D6D]">—</span>
                      ) : (
                        row.value
                      )}
                    </div>
                  </Fragment>
                );
              })}
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-[12px] font-medium text-[#222]">
                Image of Conflicting Mark
              </span>
              <div className="w-full min-h-[160px] rounded-[8px] border border-[#EEEEEE] bg-[#F7F7F7] flex items-center justify-center overflow-hidden">
                {activeItem.conflictingMarkImageUrl ? (
                  <img
                    src={activeItem.conflictingMarkImageUrl}
                    alt="Conflicting mark"
                    className="max-h-[280px] object-contain"
                  />
                ) : (
                  <span className="text-xs text-[#6D6D6D] px-2.5 py-2">
                    No image uploaded.
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* —— Editable section —— */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>No. of Paragraphs in Conflicting Proprietor&apos;s Intro</label>
          <input
            type="number"
            min={0}
            step={1}
            value={activeDraft.noOfParagraphsInConflictingIntro}
            onChange={(e) => updateDraft({ noOfParagraphsInConflictingIntro: e.target.value })}
            className={INPUT_CLASS}
            placeholder="Text Here"
            disabled={saving}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Date of Evidence Affidavit</label>
          <DateTimePicker
            value={activeDraft.dateOfEvidenceAffidavit}
            onChange={(d) => updateDraft({ dateOfEvidenceAffidavit: d })}
            showTime={false}
            placeholder="Select Date"
            buttonClassName="!h-9 !min-h-0 !text-[12px] rounded-[6px]"
          />
        </div>
      </div>

      {/* Paragraph-wise replies */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <label className={LABEL_CLASS}>Paragraph-wise Replies</label>
          <button
            type="button"
            onClick={addReplyRow}
            disabled={saving}
            className="h-8 px-3 text-[11px] font-medium text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[8px] hover:bg-gray-50 disabled:opacity-60"
          >
            + Add Reply
          </button>
        </div>
        {activeDraft.paragraphWiseReplies.map((r, idx) => (
          <div
            key={idx}
            className="flex flex-col gap-2 rounded-[6px] border border-[#EEEEEE] p-3 bg-white"
          >
            <div className="flex items-start justify-between gap-2">
              <input
                type="text"
                placeholder='e.g. "Paragraphs 1 to 5"'
                value={r.heading}
                onChange={(e) => updateReply(idx, { heading: e.target.value })}
                className={INPUT_CLASS}
                disabled={saving}
              />
              {activeDraft.paragraphWiseReplies.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeReplyRow(idx)}
                  disabled={saving}
                  className="h-9 px-3 text-[11px] font-medium text-[#D92D20] bg-white border border-[#EEEEEE] rounded-[6px] hover:bg-[#F7F7F7] whitespace-nowrap disabled:opacity-60"
                >
                  Remove
                </button>
              )}
            </div>
            <textarea
              placeholder="Reply text…"
              value={r.text}
              onChange={(e) => updateReply(idx, { text: e.target.value })}
              className={TEXTAREA_CLASS}
              rows={4}
              disabled={saving}
            />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Name of Opponent&apos;s Attorney</label>
          <input
            type="text"
            value={activeDraft.nameOfOpponentsAttorney}
            onChange={(e) => updateDraft({ nameOfOpponentsAttorney: e.target.value })}
            className={INPUT_CLASS}
            placeholder="Text Here"
            disabled={saving}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Email of Opponent&apos;s Attorney</label>
          <input
            type="email"
            value={activeDraft.emailOfOpponentsAttorney}
            onChange={(e) => updateDraft({ emailOfOpponentsAttorney: e.target.value })}
            className={INPUT_CLASS}
            placeholder="attorney@example.com"
            disabled={saving}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <label className={LABEL_CLASS}>Address of Opponent&apos;s Attorney</label>
        <textarea
          value={activeDraft.addressOfOpponentsAttorney}
          onChange={(e) => updateDraft({ addressOfOpponentsAttorney: e.target.value })}
          className={TEXTAREA_CLASS}
          rows={3}
          placeholder="Text Here"
          disabled={saving}
        />
      </div>

      {/* PDF actions */}
      <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5">
        <div className="flex flex-row items-center gap-2.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] pl-4 pr-2 py-2 min-h-[44px] sm:h-11">
          <img src={invoiceGenerate} alt="" className="w-[22px] h-[22px] flex-shrink-0" />
          <button
            type="button"
            onClick={handleGeneratePdf}
            disabled={generating || saving}
            className="flex flex-row items-center justify-center min-h-[40px] h-9 px-4 rounded-[6px] bg-[#222222] text-[12px] font-medium text-white hover:bg-[#333333] transition-colors shrink-0 disabled:opacity-60"
          >
            {generating
              ? "Generating..."
              : activeItem.evidenceAffidavitPdfUrl
                ? "Regenerate PDF"
                : "Generate PDF"}
          </button>
        </div>
        <button
          type="button"
          onClick={handlePreviewPdf}
          disabled={!activeItem.evidenceAffidavitPdfUrl || generating}
          className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Preview PDF
        </button>
        <button
          type="button"
          onClick={handleDownloadPdf}
          disabled={!activeItem.evidenceAffidavitPdfUrl || downloading || generating}
          className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {downloading ? "Downloading…" : "Download PDF"}
        </button>
      </div>
    </form>
  );
}
